import { Trash2 } from "lucide-react";
import React, { useRef } from "react";
import { DriveMapping } from "../../models/ClientConfiguration/DriveMapping";
import AddableList from "../common/AddableList";
import FieldError from "../common/FieldError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface DriveMappingControlProps {
  driveMappings: DriveMapping[];
  onChange: (driveMappings: DriveMapping[]) => void;
  disabled?: boolean;
}

const DriveMappingsControls: React.FC<DriveMappingControlProps> = ({
  driveMappings,
  onChange,
  disabled = false,
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const handleAddDriveMapping = () => {
    const newDriveMapping: DriveMapping = {
      mapFrom: "",
      mapTo: "",
    };
    const updatedDriveMappings = [...driveMappings, newDriveMapping];
    onChange(updatedDriveMappings);
  };

  const handleDeleteDriveMapping = (index: number) => {
    const updatedDriveMappings = [...driveMappings];
    updatedDriveMappings.splice(index, 1);
    onChange(updatedDriveMappings);
  };

  const handleDriveMappingChange = (
    index: number,
    field: keyof DriveMapping,
    value: string,
  ) => {
    const updatedDriveMappings = [...driveMappings];
    updatedDriveMappings[index][field] = value;
    onChange(updatedDriveMappings);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      inputRefs.current[index].blur();
    }
  };

  return (
    <div>
      <AddableList
        onAddClick={handleAddDriveMapping}
        subheaderText="Drive Mappings"
        disabled={disabled}
      >
        {driveMappings.map((driveMapping, index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="grid grid-cols-12 gap-2 w-full">
                <div className="col-span-6 min-w-[50%]">
                  <Input
                    className="h-8"
                    placeholder="Map From"
                    value={driveMapping.mapFrom || ""}
                    onChange={(event) =>
                      handleDriveMappingChange(
                        index,
                        "mapFrom",
                        event.target.value,
                      )
                    }
                    onKeyDown={(event) =>
                      handleKeyPress(
                        event as React.KeyboardEvent<HTMLInputElement>,
                        index,
                      )
                    }
                    disabled={disabled}
                  />
                  <FieldError name={`driveMappings.${index}.mapFrom`} />
                </div>
                <div className="col-span-6 min-w-[50%]">
                  <Input
                    className="h-8"
                    placeholder="Map To"
                    value={driveMapping.mapTo || ""}
                    onChange={(event) =>
                      handleDriveMappingChange(
                        index,
                        "mapTo",
                        event.target.value,
                      )
                    }
                    onKeyDown={(event) =>
                      handleKeyPress(
                        event as React.KeyboardEvent<HTMLInputElement>,
                        index,
                      )
                    }
                    disabled={disabled}
                  />
                  <FieldError name={`driveMappings.${index}.mapTo`} />
                </div>
              </div>
              <Button
                aria-label="Delete"
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => handleDeleteDriveMapping(index)}
                disabled={disabled}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </AddableList>
    </div>
  );
};

export default DriveMappingsControls;
